import React, { useState, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import styles from "./Gosi.module.css"; // 스타일 파일 경로
import * as gosiFunctions from "../../../functions/Functions";
import { formatDate } from "../../../components/Util/DateFormat";
import GosiDetailPopup from "./Popup/GosiDetailPopup";
import GosiRegisterPopup from "./Popup/GosiRegisterPopup";
import Swal from "sweetalert2";
import axios from "axios";
import { getServerBaseUrl } from "../../../utils/portConfig";

function Gosi() {
  const [gosiData, setGosiData] = useState([]); // 고시 데이터
  const [selectedRow, setSelectedRow] = useState(null); // 선택된 행
  const [isPopupOpen, setIsPopupOpen] = useState(false); // 팝업 열림 상태
  const [popupType, setPopupType] = useState(null); // 팝업 종류
  const [keyword, setKeyword] = useState(""); // 검색어
  const [selectedCategory, setSelectedCategory] = useState(""); // 선택된 분류
  const [selectedSubCategory, setSelectedSubCategory] = useState(""); // 선택된 상세분류
  const [categories, setCategories] = useState([]); // 분류 목록
  const [subCategories, setSubCategories] = useState([]); // 상세분류 목록
  const [showGosiPopup, setShowGosiPopup] = useState(false); // 고시 상세 정보 팝업 표시 여부
  const [popupData, setPopupData] = useState(null); // 팝업 데이터
  const [gridReload, setGridReload] = useState(false); // 그리드 다시 로드 플래그

  // 테이블 높이 설정값
  const [pageSize, setPageSize] = useState(10); // 기본값 10으로 시작
  const gridContainerRef = useRef(null);

  const chkAuth = localStorage.getItem("auth"); // 권한 분리
  const gridRef = useRef(null);

  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1/gosi`; // API 서버 URL
  const token = localStorage.getItem("token"); // 토큰
  const userId = localStorage.getItem("userid");

  // 화면이 그려질 때 사용자 데이터 조회
  useEffect(() => {
    setSelectedCategory("");
    setSelectedSubCategory("");
    getData(); // 초기 로드에서 검색 매개 변수 없이 호출
    getCategories();
  }, [gridReload]);

  // 선택된 카테고리가 변경될 때 실행되는 useEffect
  useEffect(() => {
    setSelectedSubCategory(""); // selectedCategory가 변경될 때 selectedSubCategory를 초기화
    setSubCategories([]);
    getSubCategories();
  }, [selectedCategory]);

  // 카테고리 목록을 가져오는 함수
  const getCategories = async () => {
    try {
      const response = await axios.get(`${API_SERVER_URL}/groups`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // "code"와 "text" 필드를 모두 가져와서 "text"를 표시하고 "code"를 값으로 사용
      const categoriesData = response.data
        .filter((item) => !isNaN(item.code) && item.code)
        .map((mItem) => ({
          value: mItem.code,
          label: mItem.text,
        }));

      setCategories(categoriesData);
    } catch (error) {
      console.error("Category fetch error:", error);
      Swal.fire({
        title: "오류!",
        text: "데이터를 가져오는 중 오류가 발생했습니다.",
        icon: "error",
        showConfirmButton: true,
      });
    }
  };

  // 하위 카테고리 목록을 가져오는 함수
  const getSubCategories = async () => {
    if (selectedCategory) {
      try {
        const response = await axios.get(`${API_SERVER_URL}/groups/${selectedCategory}/detail`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setSubCategories(response.data);
      } catch (error) {
        console.error("Subcategory fetch error:", error);
        Swal.fire({
          title: "오류!",
          text: "데이터를 가져오는 중 오류가 발생했습니다.",
          icon: "error",
          showConfirmButton: true, // 사용자가 확인 버튼을 눌러야 닫힙니다.
        });
      }
    } else {
      // If no category is selected, clear the subcategories
      setSubCategories([]);
      setSelectedSubCategory("");
    }
  };

  // 검색 버튼 클릭 시 실행되는 함수
  const handleSearch = () => {
    // 필터 상태 초기화
    const gridApi = gridRef.current.api;
    gridApi.setFilterModel(null);

    const searchParams = {
      grp: selectedCategory,
      grpDetail: selectedSubCategory,
      keyword: keyword,
    };

    getData(searchParams);
  };

  // 데이터 가져오기 함수 추가
  const getData = async (params) => {
    try {
      // 데이터 로딩 인디케이터 표시
      Swal.fire({
        title: "데이터를 가져오는 중...",
        text: "잠시만 기다려 주세요.",
        didOpen: () => {
          Swal.showLoading();
        },
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
      });

      // API 호출 및 데이터 가져오기
      const response = await axios.get(API_SERVER_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params, // 검색 매개 변수 전달
      });

      // 가져온 데이터를 상태에 설정
      setGosiData(response.data);
    } catch (error) {
      console.error("Data fetch error:", error);
      Swal.fire({
        title: "오류!",
        text: "데이터를 가져오는 중 오류가 발생했습니다.",
        icon: "error",
        showConfirmButton: true, // 사용자가 확인 버튼을 눌러야 닫힙니다.
      });
    } finally {
      // 데이터 가져오기 완료 후 로딩 인디케이터 숨기기
      Swal.close();
    }
  };

  // 데이터 수집 함수
  const handleCollect = async () => {
    // 1. 로딩 알림 표시 시작
    Swal.fire({
      title: "데이터를 수집하는 중...",
      text: "잠시만 기다려 주세요.",
      didOpen: () => {
        Swal.showLoading();
      },
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false, // 확인 버튼을 숨깁니다.
    });
    const gosiFile = new FormData();
    gosiFile.append("gb", "file");
    try {
      const response = await axios.post(
        `${API_SERVER_URL}/collect`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        // 요청이 성공하면 Swal로 메시지 출력
        Swal.fire({
          title: "성공!",
          text: "데이터를 성공적으로 수집했습니다.",
          icon: "success",
          showConfirmButton: true, // 확인 버튼을 다시 표시합니다.
        }).then(() => {
          Swal.close();
        });
      } else {
        console.error("Data fetch error:", response.statusText);
        Swal.fire({
          title: "오류!",
          text: "데이터를 가져오는 중 오류가 발생했습니다.",
          icon: "error",
          showConfirmButton: true, // 사용자가 확인 버튼을 눌러야 닫힙니다.
        });
      }
    } catch (error) {
      console.error("Data fetch error:", error);
      Swal.fire({
        title: "오류!",
        text: "데이터를 가져오는 중 오류가 발생했습니다.",
        icon: "error",
        showConfirmButton: true, // 사용자가 확인 버튼을 눌러야 닫힙니다.
      });
    }
  };

  // 그리드 컬럼 정의
  const columns = [
    {
      field: "sno",
      headerName: "고시번호",
      width: 90,
      suppressMenu: true,
    },
    {
      field: "regsno",
      headerName: "등록번호",
      width: 110,
    },
    {
      field: "title",
      headerName: "제목",
      cellStyle: { textAlign: "left", cursor: "pointer" },
      cellRenderer: cellRenderer,
      flex: 1,
    },
    {
      field: "regdt",
      headerName: "등록일",
      valueFormatter: (params) => formatDate(params.data.regdt),
      width: 120,
    },
    {
      field: "evidence",
      headerName: "관련근거",
      width: 150,
    },
    {
      field: "grpName",
      headerName: "분류",
      width: 100,
    },
    {
      field: "grpDetailName",
      headerName: "상세분류",
      width: 130,
    },
    {
      field: "id",
      hide: true, // 숨겨진 필드
    },
  ];

  // 그리드 옵션
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
    };
  }, []);

  // tooltip 기능
  function cellRenderer(params) {
    return (
      <span className="title-cell" title={params.value}>
        {params.value}
      </span>
    );
  }

  // row 선택 이벤트
  const selectRow = (event) => {
    gosiFunctions.selectRow(event, setSelectedRow);
  };

  // 생성 팝업 열기
  const OpenPopup = () => {
    gosiFunctions.OpenCreatePopup(setIsPopupOpen, setPopupType);
  };

  // 팝업 닫기
  const ClosePopup = () => {
    gosiFunctions.ClosePopup(setIsPopupOpen);
    if (gridRef.current) {
      gridRef.current.api.deselectAll();
      setSelectedRow(null);
    }
  };

  // 셀 클릭 이벤트 처리
  const onCellClicked = async (params) => {
    if (params.colDef.headerName === "제목") {
      try {
        const details = await fetchDetails(`${API_SERVER_URL}/${params.data.id}`);

        if (!details) {
          // 상세 정보를 가져오지 못한 경우 오류 메시지 표시
          Swal.fire({
            title: "오류!",
            text: "데이터를 가져오는 중 오류가 발생했습니다.",
            icon: "error",
            showConfirmButton: true, // 사용자가 확인 버튼을 눌러야 닫힘
          });
          return;
        }

        setPopupData(details); // 상세 정보를 상태에 설정
        setShowGosiPopup(true); // 팝업 표시
      } catch (error) {
        console.error("데이터 가져오기 오류:", error);
        // 오류 처리를 원하는 대로 수행할 수 있습니다.
      }
    }
  };

  // 상세 정보 가져오기
  const fetchDetails = async (url) => {
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status !== 200) {
        throw new Error("상세 정보를 가져오는 중 오류가 발생했습니다.");
      }

      return response.data;
    } catch (error) {
      console.error("상세 정보 fetch 오류:", error);
      return null;
    }
  };

  // Enter 키로 검색
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 고시 등록
  const gosiSavePopup = async (gosiRegData) => {
    await gosiFunctions.createGosi(API_SERVER_URL, token, userId, gosiRegData, setIsPopupOpen, setGridReload);
  };

  // 고시 삭제
  const deleteGosi = async () => {
    await gosiFunctions.deleteGosi(API_SERVER_URL, selectedRow, token, setGridReload, setSelectedRow, setIsPopupOpen, setShowGosiPopup);
  };

  // 테이블 높이 변경 함수
  const calculatePageSize = () => {
    if (gridContainerRef.current) {
      const gridHeight = gridContainerRef.current.clientHeight;
      const rowHeight = 43; // AG Grid의 기본 행 높이 (alpine 테마)
      const headerHeight = 45; // 헤더 높이
      const paginationHeight = 45; // 페이지네이션 아래 여백

      const availableRows = Math.floor((gridHeight - headerHeight - paginationHeight) / rowHeight);

      // 최소 5개, 계산된 값보다 작은 행 수로 설정
      const newPageSize = Math.max(5, availableRows);
      setPageSize(newPageSize);
    }
  };

  // 컴포넌트 마운트 및 창 크기 변경 시 테이블 높이 조정
  useEffect(() => {
    calculatePageSize();

    // 창 크기 변경 이벤트 리스너 추가
    window.addEventListener("resize", calculatePageSize);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener("resize", calculatePageSize);
    };
  }, []);

  // 데이터 로드 후 그리드 크기에 맞게 테이블 높이 재계산
  useEffect(() => {
    if (gosiData.length > 0) {
      setTimeout(calculatePageSize, 100); // 데이터 로드 후 약간의 지연을 두고 계산
    }
  }, [gosiData]);

  return (
    <div className="dashboard-content">
      <div className="dashboard-content-container">
        <div className="dashboard-content-header">
          <h2>고시 관리</h2>
        </div>

        <div className={styles["search-container"]}>
          <div className={styles["search-group"]}>
            {/* 분류 선택 */}
            <div className="search-field">
              <label>분류</label>
              <select className={styles["dashboard-content-select"]} value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                <option value="">분류 선택</option>
                {categories.map((category, index) => (
                  <option key={index} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
            {/* 상세분류 선택 */}
            <div className="search-field">
              <label>상세분류</label>
              <select className={styles["dashboard-content-select"]} value={selectedSubCategory} onChange={(e) => setSelectedSubCategory(e.target.value)}>
                <option value="">상세분류 선택</option>
                {subCategories.map((subCategory, index) => (
                  <option key={index} value={subCategory.code}>
                    {subCategory.text}
                  </option>
                ))}
              </select>
            </div>
            <div className="search-field">
              <label>키워드</label>
              <input className={styles["dashboard-content-select"]} type="text" placeholder="키워드 입력" value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyPress={handleKeyPress} />
            </div>
            <button className={styles["green-button"]} onClick={handleSearch}>
              검색
            </button>
          </div>
          <div className="button-group">
            {chkAuth === "Superadmin" && (
              <button className={styles["gray-button"]} onClick={OpenPopup}>
                등록
              </button>
            )}
            {chkAuth === "Superadmin" && (
              <button className={styles["blue-button"]} onClick={handleCollect}>
                수집
              </button>
            )}
          </div>
        </div>

        {showGosiPopup && <GosiDetailPopup data={popupData} selectedRow={selectedRow} onDelete={deleteGosi} onClose={() => setShowGosiPopup(false)} token={token} />}

        <GosiRegisterPopup isOpen={isPopupOpen && popupType === "create"} onClose={ClosePopup} onSave={gosiSavePopup} />

        <div ref={gridContainerRef} className="ag-theme-alpine" style={{ height: "100%", width: "100%", fontWeight: "400" }}>
          <AgGridReact
            ref={(grid) => (gridRef.current = grid)}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={pageSize}
            defaultColDef={defaultColDef}
            rowData={gosiData} // Ag-Grid의 데이터 소스
            onRowSelected={selectRow} // 행 선택 처리
            rowSelection="single" // 단일 행 선택
            domLayout="autoHeight"
            multiSortKey="shift"
            onCellClicked={onCellClicked}
          />
        </div>
      </div>
    </div>
  );
}

export default Gosi;
